import { produce } from 'immer'
import { StoreSlice, StoreState } from '.'
import { dateInputValuesToWeekDates, endOfWeek, formatISODate, startOfWeek } from '../utils/date'
import { Organization } from '../api/organizations'
import { JobTitle } from '../api/job-titles'
import { IsoDate } from '../hooks/use-calendar'
import { MissionContractModel } from '../../../backend/src/services/resources/missions/mission.model'
import { endOfDay, startOfDay, startOfMonth, subDays } from 'date-fns'
import { cloneDeep } from 'lodash'
import { WorkPeriodEvent } from '../api/work-period-events'
import { User } from '../api/users'
import { Service } from '../api/services'
import { WorkPeriodTag } from '../api/work-period-tags'
import { ClockingStatusEnum } from '../utils/clockings'
import { PeriodSummaryStatus } from '../api/period-summaries'

export enum FiltersResourcesEnum {
  missions = 'missions',
  workersRequests = 'workersRequests',
  workPeriods = 'workPeriods',
  clockings = 'clockings',
  preBillings = 'preBillings',
  kPI = 'kPI',
  laborCostsCoefficients = 'laborCostsCoefficients',
  payroll = 'payroll',
}

export interface MissionsFilters {
  start: Date
  end: Date
  service: string[]
  interimAgency: Organization['_id'][]
  employer: Organization['_id'][]
  displayDay?: Date
  jobTitle: JobTitle['_id'][]
  events?: WorkPeriodEvent['_id'][]
  tags?: WorkPeriodTag['_id'][]
  summaryMinimalMode: boolean
  user: string[]
  isCancelled: boolean[]
  'workPeriods.periodSummary': string[] | boolean[]
  periodSummary: string[] | boolean[]
  contractModel: MissionContractModel[]
}

export interface WorkPeriodsFilters {
  start: IsoDate
  end: IsoDate
  interimAgency?: Organization['_id'][]
  employer?: Organization['_id'][]
  jobTitle?: JobTitle['_id'][]
  service?: MissionsFilters['service']
  events?: WorkPeriodEvent['_id'][]
  tags?: WorkPeriodTag['_id'][]
  user?: string[]
  isValidated?: boolean[]
  isAbandoned?: boolean[]
  isCancelled?: boolean[]
}

export interface WorkersRequestsFilters {
  service?: MissionsFilters['service']
  interimAgency?: MissionsFilters['interimAgency']
  jobTitle?: MissionsFilters['jobTitle']
  employer?: MissionsFilters['employer']
  isCancelled?: boolean[]
  isComplete?: boolean[]
  isExpired?: Date
  start?: Date
  end?: Date
}

export interface KPIFilters {
  service?: MissionsFilters['service']
  interimAgency?: MissionsFilters['interimAgency']
  jobTitle?: MissionsFilters['jobTitle']
  employer?: MissionsFilters['employer']
  user?: MissionsFilters['user']
  start: IsoDate
  end: IsoDate
}

export interface ClockingsFilters {
  user?: MissionsFilters['user']
  start?: MissionsFilters['start']
  end?: MissionsFilters['end']
  type?: ClockingStatusEnum[]
}

export interface PreBillingsFilters {
  start?: MissionsFilters['start']
  end?: MissionsFilters['end']
  interimAgency?: MissionsFilters['interimAgency']
  invoiceRef?: boolean[]
  status?: PeriodSummaryStatus[]
}

export interface LaborCostsCoefficientsFilters {
  interimAgency?: Organization['_id']
  jobTitle?: JobTitle['_id']
}

export type PayrollWeekStatus = 'closed' | 'readyToClose' | 'needsValidation'

export interface PayrollFilters {
  start?: Date
  end?: Date
  service?: Service['_id'][]
  user?: User['_id'][]
  jobTitle?: JobTitle['_id'][]
  weekStatuses?: PayrollWeekStatus[]
  currentWorkerId?: string
}

interface PaginationItem {
  currentPage: number
  itemsPerPage: number
}

interface Pagination {
  summary: PaginationItem
}

export interface FiltersState {
  [FiltersResourcesEnum.missions]: MissionsFilters
  [FiltersResourcesEnum.workersRequests]: WorkersRequestsFilters
  [FiltersResourcesEnum.workPeriods]: WorkPeriodsFilters
  [FiltersResourcesEnum.clockings]: ClockingsFilters
  [FiltersResourcesEnum.preBillings]: PreBillingsFilters
  [FiltersResourcesEnum.kPI]: KPIFilters
  [FiltersResourcesEnum.laborCostsCoefficients]: LaborCostsCoefficientsFilters
  [FiltersResourcesEnum.payroll]: PayrollFilters
}

export interface FiltersSlice extends FiltersState {
  setFilters<Resource extends FiltersResourcesEnum>(
    resource: Resource,
    filters: FiltersState[Resource],
  ): void
  pagination: Pagination
  setPagination(item: keyof Pagination, value: PaginationItem): void
}

const now = new Date()
const initialStartEndisThisWeek = { start: startOfWeek(now), end: endOfWeek(now) }
const initialStartEndisToday = {
  start: formatISODate(now),
  end: formatISODate(now),
}

/**
 * Initial state values for each filter type
 */
export const initialFilters: FiltersState = {
  [FiltersResourcesEnum.missions]: {
    ...initialStartEndisThisWeek,
    periodSummary: [],
    isCancelled: [],
    start: dateInputValuesToWeekDates(formatISODate(new Date())).start,
    end: dateInputValuesToWeekDates(formatISODate(new Date())).end,
    employer: [],
    interimAgency: [],
    jobTitle: [],
    user: [],
    events: [],
    tags: [],
    service: [],
    'workPeriods.periodSummary': [],
    contractModel: [],
    summaryMinimalMode: true,
  },
  [FiltersResourcesEnum.workPeriods]: {
    ...initialStartEndisToday,
    events: [],
    tags: [],
  },
  [FiltersResourcesEnum.kPI]: {
    ...initialStartEndisToday,
  },
  [FiltersResourcesEnum.workersRequests]: {
    isExpired: subDays(startOfDay(now), 3),
    isComplete: [false],
    isCancelled: [false],
  },
  [FiltersResourcesEnum.clockings]: {
    start: startOfDay(now),
    end: endOfDay(now),
  },
  [FiltersResourcesEnum.preBillings]: {
    invoiceRef: [false],
    status: [],
  },
  [FiltersResourcesEnum.laborCostsCoefficients]: {
    interimAgency: undefined,
    jobTitle: undefined,
  },
  [FiltersResourcesEnum.payroll]: {
    end: endOfWeek(now),
    start: startOfMonth(subDays(now, 24)),
    service: undefined,
    jobTitle: undefined,
    user: [],
    weekStatuses: [],
    currentWorkerId: undefined,
  },
}

const filtersSlice: StoreSlice<FiltersSlice> = set => ({
  ...cloneDeep(initialFilters),
  pagination: {
    summary: { currentPage: 1, itemsPerPage: 20 },
  },
  setFilters: (resource, filters) => {
    set(
      produce(state => {
        state.filters[resource] = filters
      }),
    )
  },
  setPagination: (item, value) => {
    set(
      produce((state: StoreState) => {
        state.filters.pagination[item] = value
      }),
    )
  },
})

export default filtersSlice
